<!--
  responsibleForm: presentational form of a responsible (Ansprechpartner:in) without any API call of its own
  (except the optional createJobTypeDialog, only rendered with permissions.createJobType).
  Used by createResponsibleDialog and editResponsibleDialog; meant to be reused by other views with their own API calls.
  Must be placed inside a <v-form> (fields register their rules there). The sections carry data-section="person|position|contact"
  for the steps of CreateStepperDialog.

  Props
    value         ResponsibleFormValue (required)  plain state, see responsibleForm.helper.ts (emptyResponsibleForm / responsibleFormFromEntity)
    jobTypes      JobTypeItem[]   ({ text, value: ObjectId })   items of the job position autocomplete (loadJobTypeItems())
    loading       Boolean   shows skeletons instead of the fields
    jobTypesLoading Boolean loading state of the job position autocomplete
    disabled      Boolean   disables all inputs (e.g. while saving)
    permissions   Partial<ResponsibleFormPermissions>  field groups to show, missing keys count as true (createJobType as false)
    requireFirstName Boolean  first name required (default false, last name and gender are always required)

  Events
    input            (value: ResponsibleFormValue)  new state object on every change (v-model)
    job-type-created (jobType: JobType)              after a job position was created inside the form (value.jobType is set already);
                                                     append it to your jobTypes items
-->
<template>
  <div class="rf mdlg-scope">
    <div v-if="loading" class="d-flex flex-column py-4" style="gap: 14px">
      <v-skeleton-loader max-width="160" type="text"/>
      <v-skeleton-loader class="rounded-lg" height="38" type="image"/>
      <v-skeleton-loader class="rounded-lg" height="40" type="image"/>
      <v-skeleton-loader max-width="160" type="text"/>
      <v-skeleton-loader class="rounded-lg" height="40" type="image"/>
    </div>
    <template v-else>
      <section v-if="perms.gender || perms.firstName || perms.lastName || perms.birthday" class="mdlg-section rf__section" data-section="person">
        <div class="mdlg-section__title"><v-icon small>mdi-account-outline</v-icon>Person</div>
        <div v-if="perms.gender" class="mdlg-field">
          <span class="acq-label">Anrede<span class="mdlg-req"> *</span></span>
          <v-input :rules="[RuleFactory.required('Bitte eine Anrede wählen')]" :value="value.gender" class="rf__plain" hide-details="auto">
            <segmented-control :block="$vuetify.breakpoint.xsOnly" :items="genderItems" :value="value.gender" label="Anrede"
                               @input="!disabled && set('gender', $event)"/>
          </v-input>
        </div>
        <div v-if="perms.firstName || perms.lastName || perms.birthday" :style="{ '--rf-cols': nameColumns }" class="rf__names soft-inputs">
          <div v-if="perms.firstName" class="mdlg-field">
            <span class="acq-label">{{ isFamily ? 'Vornamen' : 'Vorname' }}<span v-if="requireFirstName" class="mdlg-req"> *</span></span>
            <v-text-field :disabled="disabled" :rules="requireFirstName ? [RuleFactory.required('Dieses Feld ist erforderlich.')] : []"
                          :value="value.firstName" autocomplete="off" color="info" dense hide-details="auto" outlined
                          placeholder="z. B. Maria" @input="set('firstName', $event || '')"/>
          </div>
          <div v-if="perms.lastName" class="mdlg-field">
            <span class="acq-label">Nachname<span class="mdlg-req"> *</span></span>
            <v-text-field :disabled="disabled" :rules="[RuleFactory.required('Dieses Feld ist erforderlich.')]" :value="value.lastName"
                          autocomplete="off" color="info" dense hide-details="auto" outlined placeholder="z. B. Schneider"
                          @input="set('lastName', $event || '')"/>
          </div>
          <div v-if="perms.birthday" class="mdlg-field">
            <span class="acq-label">Geburtstag <span class="acq-tiny">(optional)</span></span>
            <date-picker :disabled="disabled" :rounded="false" :value="value.birthday" birthday-mode clearable color="info" dense label="" outlined
                         @input="set('birthday', $event || null)"/>
          </div>
        </div>
      </section>

      <section v-if="perms.jobType" class="mdlg-section rf__section" data-section="position">
        <div class="mdlg-section__title"><v-icon small>mdi-briefcase-outline</v-icon>Position</div>
        <div class="mdlg-field soft-inputs">
          <span class="acq-label">Jobposition</span>
          <div class="rf__job">
            <v-autocomplete :disabled="disabled" :items="jobTypes" :loading="jobTypesLoading" :value="value.jobType" class="rf__job-input" clearable
                            color="info" dense hide-details="auto" item-color="info" no-data-text="Keine passende Jobposition gefunden" outlined
                            placeholder="Tippen um zu suchen, z. B. Schulleitung" prepend-inner-icon="mdi-magnify"
                            @input="set('jobType', $event || null)"/>
            <v-tooltip v-if="perms.createJobType" bottom open-delay="300">
              <template #activator="{ on, attrs }">
                <button :disabled="disabled" aria-label="Neue Jobposition anlegen" class="rf__job-add" type="button" v-bind="attrs"
                        @click="showCreateJobType = true" v-on="on">
                  <v-icon small>mdi-plus</v-icon>
                </button>
              </template>
              <span>Neue Jobposition anlegen</span>
            </v-tooltip>
          </div>
        </div>
      </section>

      <section v-if="perms.contactInformation" class="mdlg-section rf__section" data-section="contact">
        <div class="mdlg-section__title"><v-icon small>mdi-card-account-phone-outline</v-icon>Kontakt</div>
        <contact-fields-editor :disabled="disabled" :value="value.contactInformation" is-business-contact layout="compact"
                               @input="set('contactInformation', $event)"/>
      </section>
    </template>

    <create-job-type v-if="perms.createJobType" v-model="showCreateJobType" @update:jobType="onJobTypeCreated"/>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {GenderTypes} from '@/enums/GenderTypes.enum';
import {RuleFactory} from '@/helpers/ruleFactory.helper';
import {JobType} from '@/interfaces/jobType.interface';
import DatePicker from '@/components/common/datePicker.vue';
import SegmentedControl, {SegmentItem} from '@/components/common/modern/segmentedControl.vue';
import ContactFieldsEditor from '@/components/common/modern/contactFieldsEditor.vue';
import CreateJobType from '@/components/responsibles/createJobTypeDialog.vue';
import {
  FULL_RESPONSIBLE_FORM_PERMISSIONS,
  JobTypeItem,
  ResponsibleFormPermissions,
  ResponsibleFormValue,
} from '@/components/responsibles/responsibleForm.helper';

export default Vue.extend({
  components: {ContactFieldsEditor, CreateJobType, DatePicker, SegmentedControl},
  props: {
    value: {type: Object as () => ResponsibleFormValue, required: true},
    jobTypes: {type: Array as () => JobTypeItem[], default: () => []},
    loading: {type: Boolean, default: false},
    jobTypesLoading: {type: Boolean, default: false},
    disabled: {type: Boolean, default: false},
    permissions: {type: Object as () => Partial<ResponsibleFormPermissions>, default: () => ({})},
    requireFirstName: {type: Boolean, default: false},
  },
  data: () => ({
    showCreateJobType: false,
  }),
  computed: {
    RuleFactory: () => RuleFactory,
    perms(): ResponsibleFormPermissions {
      return {...FULL_RESPONSIBLE_FORM_PERMISSIONS, ...this.permissions};
    },
    isFamily(): boolean {
      return this.value.gender === GenderTypes.FAMILY;
    },
    /** Vorname | Nachname | Geburtstag (narrower), depending on the visible fields */
    nameColumns(): string {
      const cols: string[] = [];
      if (this.perms.firstName) {
        cols.push('minmax(0, 1fr)');
      }
      if (this.perms.lastName) {
        cols.push('minmax(0, 1fr)');
      }
      if (this.perms.birthday) {
        cols.push('minmax(160px, 200px)');
      }
      return cols.join(' ');
    },
    genderItems(): SegmentItem[] {
      return GenderTypes.Items.map((item) => ({value: item.value, text: item.salutation || item.text, icon: item.icon}));
    },
  },
  methods: {
    set<K extends keyof ResponsibleFormValue>(key: K, v: ResponsibleFormValue[K]) {
      this.$emit('input', {...this.value, [key]: v});
    },
    onJobTypeCreated(jobType: JobType) {
      this.set('jobType', jobType._id);
      this.$emit('job-type-created', jobType);
    },
  },
});
</script>

<style scoped>
.rf__plain {
  margin: 0;
  padding: 0;
}

.rf__plain >>> .v-input__slot {
  margin-bottom: 0;
  display: block;
}

.rf__section {
  gap: 10px;
  padding: 12px 0;
}

.rf__section:first-of-type {
  padding-top: 4px;
}

.rf__section:last-of-type {
  padding-bottom: 4px;
}

.rf__names {
  display: grid;
  grid-template-columns: var(--rf-cols, repeat(2, minmax(0, 1fr)));
  gap: 12px 16px;
}

@media (max-width: 599px) {
  .rf__names {
    grid-template-columns: minmax(0, 1fr);
  }
}

.rf__job {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.rf__job-input {
  flex: 1 1 auto;
  min-width: 0;
}

.rf__job-add {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 10px;
  background: rgba(25, 118, 210, .08);
  color: #1976D2;
  cursor: pointer;
  transition: background .15s ease;
}

.rf__job-add:hover:not(:disabled) {
  background: rgba(25, 118, 210, .16);
}

.rf__job-add:disabled {
  opacity: .5;
  cursor: default;
}

.rf__job-add .v-icon {
  color: inherit !important;
}

.theme--dark .rf__job-add {
  background: rgba(144, 202, 249, .12);
  color: #90CAF9;
}
</style>
<style src="@/components/acquisitionList/ui/acquisitionViews.css"></style>
<style src="@/components/common/modern/modern.css"></style>
<style src="@/components/common/modern/modernDialog.css"></style>
